import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, ShoppingBag, Sparkles } from 'lucide-react';
import { useCart } from '../App';

/**
 * Reusable product popup with gender-specific themes
 * gender: 'men' | 'women' | null (default)
 */
const ProductPopup = ({ product, onClose, gender = null }) => {
  const { addToCart } = useCart();
  if (!product) return null;

  const g = gender || product.gender || 'men';

  const themes = {
    men: {
      bg: 'linear-gradient(180deg, #141414 0%, #0a0a0a 50%, #080808 100%)',
      border: '#3a3a3a',
      accent: '#a89878',
      accentDim: '#6a5a4a',
      textPrimary: '#e0d8cc',
      textSecondary: '#8a8278',
      noteBg: 'rgba(60,60,60,0.4)',
      noteBorder: '#3a3a3a',
      btnBg: 'linear-gradient(180deg, #4a4a4a, #2a2a2a)',
      btnColor: '#c9b998',
      overlay: 'rgba(10,10,10,0.96)',
      imgOverlay: 'linear-gradient(to top, #141414 0%, transparent 100%)',
      glowColor: 'rgba(168,152,120,0.15)',
    },
    women: {
      bg: 'linear-gradient(180deg, #1a1215 0%, #120d0f 50%, #0d090b 100%)',
      border: '#4a3540',
      accent: '#c4a082',
      accentDim: '#7a5f50',
      textPrimary: '#e8d8d0',
      textSecondary: '#a08878',
      noteBg: 'rgba(74,53,64,0.35)',
      noteBorder: '#4a3540',
      btnBg: 'linear-gradient(180deg, #5a4550, #3a2530)',
      btnColor: '#d4b8a8',
      overlay: 'rgba(18,13,15,0.96)',
      imgOverlay: 'linear-gradient(to top, #1a1215 0%, transparent 100%)',
      glowColor: 'rgba(196,160,130,0.15)',
    },
  };

  const t = themes[g] || themes.men;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="product-popup-overlay"
      style={{ background: t.overlay }}
    >
      {/* Subtle glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: `radial-gradient(ellipse at 50% 30%, ${t.glowColor} 0%, transparent 50%)`,
      }} />

      <div
        className="relative max-w-md w-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="product-popup"
        style={{
          animation: 'popupReveal 0.35s ease-out',
          background: t.bg,
          border: `2px solid ${t.border}`,
          boxShadow: `0 0 60px rgba(0,0,0,0.6), 0 30px 60px rgba(0,0,0,0.8)`,
          maxHeight: '90vh',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center transition-all hover:opacity-80"
          style={{ color: t.accent, border: `1px solid ${t.border}` }}
          data-testid="popup-close-btn"
        >
          <X size={18} />
        </button>

        {/* Product image */}
        <div className="relative h-44 overflow-hidden flex-shrink-0">
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover"
              style={{ filter: 'sepia(0.05) brightness(0.95)' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: t.bg }}>
              <span className="font-heading text-sm tracking-[0.2em]" style={{ color: t.accentDim }}>NO IMAGE YET</span>
            </div>
          )}
          <div className="absolute inset-0" style={{ background: t.imgOverlay }} />
          <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 40px rgba(0,0,0,0.5)' }} />
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-heading text-xl" style={{ color: t.textPrimary }}>{product.name}</h3>
              <p className="font-body text-sm italic" style={{ color: t.accentDim }}>{product.subtitle}</p>
            </div>
            <div className="text-right">
              {product.original_price && product.original_price > product.price && (
                <p className="font-body text-xs line-through mb-0.5" style={{ color: `${t.textSecondary}80` }}>
                  A${product.original_price.toFixed(2)}
                </p>
              )}
              {product.price > 0 && (
                <span className="font-heading text-xl" style={{ color: t.accent }}>
                  A${typeof product.price === 'number' ? product.price.toFixed(2) : product.price}
                </span>
              )}
              {product.discount_pct ? (
                <p className="font-heading text-[10px] tracking-wider mt-0.5" style={{ color: '#D4AF37' }}>
                  {product.discount_pct}% OFF
                </p>
              ) : null}
            </div>
          </div>

          <p className="font-body text-xs mb-3" style={{ color: t.accentDim }}>
            {product.category} {product.size ? `\u00B7 ${product.size}` : ''}
          </p>

          <p className="font-body text-sm leading-relaxed mb-3" style={{ color: t.textSecondary }}>
            {product.description?.length > 120 ? product.description.slice(0, 120) + '...' : product.description}
          </p>

          {/* Scent Notes */}
          {product.notes && (
            <div className="mb-3">
              <p className="font-heading text-[10px] tracking-wider mb-1.5" style={{ color: t.accent }}>SCENT NOTES</p>
              <div className="flex flex-wrap gap-1">
                {[
                  ...(product.notes.top || []),
                  ...(product.notes.heart || []),
                  ...(product.notes.base || []),
                ].slice(0, 6).map((note, i) => (
                  <span
                    key={i}
                    className="px-2.5 py-1 font-body text-[10px]"
                    style={{ color: t.accent, background: t.noteBg, border: `1px solid ${t.noteBorder}` }}
                  >
                    {note}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Scent profile feel */}
          {product.scent_profile?.feels_like && (
            <p className="font-body text-xs italic mb-4" style={{ color: `${t.textSecondary}80` }}>
              "{product.scent_profile.feels_like}"
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Link
              to={`/products/${product.id}`}
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-2 py-3 font-heading text-xs tracking-wider transition-all"
              style={{ background: t.btnBg, color: t.btnColor }}
              data-testid="popup-view-btn"
            >
              <Sparkles size={14} />
              VIEW PRODUCT
            </Link>
            <button
              onClick={() => { if (!product.in_stock) return; addToCart(product.id, 1); onClose(); }}
              disabled={!product.in_stock}
              className="flex-1 flex items-center justify-center gap-2 py-3 font-heading text-xs tracking-wider transition-all hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ border: `1px solid ${t.border}`, color: t.accent }}
              data-testid="popup-add-to-bag-btn"
            >
              <ShoppingBag size={14} />
              {product.in_stock ? 'ADD TO BAG' : 'SOLD OUT'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes popupReveal {
          0% { opacity: 0; transform: scale(0.92) translateY(20px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default ProductPopup;
